import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  Alignment,
  BlockQuote,
  Bold,
  ClassicEditor,
  Essentials,
  FontSize,
  Indent,
  Italic,
  Link,
  List,
  Paragraph,
  Undo
} from 'ckeditor5';

/** Treats empty rich-text HTML (e.g. `<p>&nbsp;</p>`) as missing. */
export function richTextRequired(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value == null || value === '') {
      return { required: true };
    }
    const text = String(value)
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return text.length ? null : { required: true };
  };
}

export function stripRichText(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return String(value)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Pull first font-size from nested styled nodes onto each <li> so markers scale. */
export function syncListItemFontSizesInElement(root: ParentNode | null | undefined): void {
  if (!root) {
    return;
  }

  root.querySelectorAll('li').forEach((li) => {
    const el = li as HTMLElement;
    const sized = el.querySelector('[style*="font-size"]') as HTMLElement | null;
    const size = sized?.style?.fontSize?.trim();
    if (size) {
      el.style.fontSize = size;
    } else {
      el.style.removeProperty('font-size');
    }
  });
}

/** Same sync for HTML strings used by [innerHTML] display. */
export function syncListItemFontSizesInHtml(html: string): string {
  if (typeof DOMParser === 'undefined' || !html) {
    return html;
  }

  const doc = new DOMParser().parseFromString(`<div id="rt-root">${html}</div>`, 'text/html');
  const root = doc.getElementById('rt-root');
  if (!root) {
    return html;
  }

  syncListItemFontSizesInElement(root);
  return root.innerHTML;
}

/** Keep bullet/number markers matching text size while editing. */
export function bindOpportunityEditorListFontSync(editor: any): void {
  if (!editor) {
    return;
  }

  const sync = () => {
    syncListItemFontSizesInElement(editor.ui?.getEditableElement?.() ?? null);
  };

  sync();
  editor.model?.document?.on('change:data', () => {
    setTimeout(sync, 0);
  });

  // Arabic UI prefers southWest panels, which open left and get clipped on the
  // first toolbar item (font size). Pin dropdowns to open to the right instead.
  pinToolbarDropdownsSouthEast(editor);
}

/** Force toolbar dropdown panels to align under the button's left edge. */
function pinToolbarDropdownsSouthEast(editor: any): void {
  const pin = () => {
    const items = editor?.ui?.view?.toolbar?.items;
    if (!items) {
      return;
    }

    for (const item of items) {
      if (item && typeof (item as { panelPosition?: string }).panelPosition !== 'undefined') {
        (item as { panelPosition: string }).panelPosition = 'se';
      }
    }
  };

  pin();
  setTimeout(pin, 0);
}

export const OpportunityEditor = ClassicEditor;

const opportunityPlugins = [
  Essentials,
  Paragraph,
  Bold,
  Italic,
  FontSize,
  Alignment,
  Link,
  List,
  Indent,
  BlockQuote,
  Undo
];

/**
 * Font-size + text alignment toolbar (no headings).
 * supportAllValues lets users mix sizes inside lists and use any px value.
 * Alignment saves text-align on blocks so left/center/right/justify match the site.
 */
export function opportunityEditorConfig(options?: {
  placeholder?: string;
  language?: string;
}): Record<string, unknown> {
  return {
    plugins: opportunityPlugins,
    placeholder: options?.placeholder ?? '',
    // Admin authors in English/LTR by default; pass language: 'ar' for Arabic content.
    language: options?.language ?? 'en',
    toolbar: {
      items: [
        'fontSize',
        '|',
        'bold',
        'italic',
        'link',
        '|',
        'alignment',
        '|',
        'bulletedList',
        'numberedList',
        '|',
        'outdent',
        'indent',
        '|',
        'blockQuote',
        'undo',
        'redo'
      ],
      shouldNotGroupWhenFull: true
    },
    fontSize: {
      options: [10, 12, 14, 'default', 16, 18, 20, 24, 28, 32, 36],
      supportAllValues: true
    },
    alignment: {
      options: ['left', 'center', 'right', 'justify']
    },
    // RTL content puts Powered by on the left by default — keep it on the right
    // so it does not sit under the font-size / alignment menus.
    ui: {
      poweredBy: {
        position: 'border',
        side: 'right',
        horizontalOffset: 8,
        verticalOffset: 5
      }
    }
  };
}
